// ============================================================
// LOADOUT ADAPTER - two-way bridge between a combatant's sheet
// weapons and the flat 40-cell Slot grid the Armory still speaks.
//
// A Slot is flat: it merges weapon-intrinsic data with the
// character-derived skillValue/strengthBonus AND live ammo. Going out,
// projectLoadout re-derives those. Coming back in, this module splits a
// Slot[] into its three homes:
//
//   weapon-intrinsic  → sheet.weapons        (a SHEET edit)
//   live ammo         → state.weaponAmmo     (a STATE edit)
//   grid position     → slotOrder            (a layout preference)
//
// slotOrder exists because the grid lets a weapon sit at ANY of the 40
// cells - drag-drop, cut/paste into a specific empty cell - while
// sheet.weapons is just an ordered list. Without it, every edit would
// silently compact the grid and move the user's weapons around.
// ============================================================
import type { Slot } from '../../types';
import type { Character, CharacterWeapon, WeaponAmmo } from '../character/types';
import { resolveAttackerStats, withoutWeaponOverrides } from '../character/combatStatsResolver';
import type { Combatant } from './types';
import { effectiveSheet } from './stancedSheet';
import { upgradeAreaSpecials } from '../areaShapes';

/** Weapon-intrinsic fields only - skillValue/strengthBonus are projected, never stored. */
function weaponFromSlot(slot: Slot, id: string): CharacterWeapon {
  // A legacy shape chip becomes the block it stood for on this save (v8.372.7).
  const upgraded = upgradeAreaSpecials(slot);
  const area = upgraded.area ?? slot.area;
  return {
    id,
    name: slot.weaponName || slot.name || 'Weapon',
    type: slot.weaponType,
    damageExpression: slot.damageExpression,
    damageType: slot.damageType,
    penetration: slot.penetration,
    range: slot.range,
    rateOfFire: { ...slot.rateOfFire },
    specialRules: upgraded.specialRules,
    psyRating: slot.psyRating ?? 0,
    // Always numbers, never undefined - the sheet is JSON-stringified and
    // a dropped field here is lost on every Armory save (v8.279.0).
    attackMod: slot.attackMod ?? 0,
    coneLength: slot.coneLength ?? 0,
    // Optional, and spread only when set: the store refuses a field that is undefined.
    ...(slot.reach ? { reach: slot.reach } : {}),
    // On a Character, ammo is magazine CAPACITY; the live count lives in state.
    ammo: { ...slot.ammo, current: slot.ammo?.max ?? 0 },
    righteousFury: slot.righteousFury,
    ...optionalOf(slot, area),
  };
}

/** The three effect lists, each copied and spread only when it has entries. */
function effectListsOf(slot: Slot): Partial<CharacterWeapon> {
  return {
    ...(slot.afflictions?.length ? { afflictions: slot.afflictions.map((a) => ({ ...a })) } : {}),
    ...(slot.exposures?.length ? { exposures: slot.exposures.map((x) => ({ ...x })) } : {}),
    ...(slot.hallucinogens?.length ? { hallucinogens: slot.hallucinogens.map((h) => ({ ...h })) } : {}),
  };
}

/**
 * The weapon's optional fields, each spread only when set: an explicit undefined kills Firestore sync, and an
 * absent field keeps a weapon reading as it did before the field existed.
 */
function optionalOf(slot: Slot, area: Slot['area']): Partial<CharacterWeapon> {
  return {
    ...effectListsOf(slot),
    ...(area && area.shape !== 'none' ? { area: { ...area } } : {}),
    ...(slot.noise ? { noise: slot.noise } : {}),
    ...(slot.rules?.length ? { rules: slot.rules } : {}),
    ...(slot.tags?.length ? { tags: [...slot.tags] } : {}),
    ...(slot.recipe ? { recipe: slot.recipe } : {}),
  };
}

let weaponSeq = 0;
/** Deterministic id minting - never Math.random(), which the E2E dice harness stubs. */
function nextWeaponId(): string {
  weaponSeq += 1;
  return `wpn-${Date.now().toString(36)}-${weaponSeq.toString(36)}`;
}

export interface LoadoutWriteback {
  weapons: CharacterWeapon[];
  weaponAmmo: Record<string, WeaponAmmo>;
  /** Dense: one id per armed plate, in rack order. */
  slotOrder: string[];
}

/**
 * The live count never above the magazine: an edit that shrinks the clip
 * takes the extra rounds with it - "28 of 10" otherwise (overnight fuzz,
 * v8.399.9). A magazine with no size is left as it is.
 */
function withinMagazine(ammo: WeaponAmmo): WeaponAmmo {
  return typeof ammo?.max === 'number' && ammo.current > ammo.max ? { ...ammo, current: ammo.max } : { ...ammo };
}

/**
 * Rack → the three destinations.
 *
 * THE WEAPON SAYS WHO IT IS. Identity comes off `slot.weaponId`, which
 * `projectLoadout` puts there. It used to be re-derived from POSITION
 * (`previousOrder[i]`), which made a weapon's id a function of where it
 * happened to sit: reordering two weapons handed each the other's id, and
 * the first save of an un-arranged combatant renamed everything it owned.
 *
 * That looked harmless because this same pass rewrites `weaponAmmo` from
 * the slots, so the counts followed the plates. It was not: `state` is
 * keyed by weapon id and outlives the save. Undo restores state onto a
 * sheet it never reverts, so a reorder followed by Ctrl+Z poured the
 * bolter's rounds into the chainsword.
 *
 * `previousOrder` survives only for slots that never came from a
 * projection - the scratch rack, a v1-v3 import, a hand-built fixture.
 */
export function slotsToWeapons(slots: Slot[], previousOrder: (string | null)[] = []): LoadoutWriteback {
  const weapons: CharacterWeapon[] = [];
  const weaponAmmo: Record<string, WeaponAmmo> = {};
  const slotOrder: string[] = [];
  const used = new Set<string>();

  // A rack has no empty plates, but this still tolerates them: the scratch
  // grid and legacy v1-v3 imports can hand over a sparse array.
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot || slot.isEmpty) continue;
    let id = slot.weaponId ?? previousOrder[i] ?? nextWeaponId();
    // A REPEATED id is a copied plate whose weaponId came along for the
    // ride. Mint, or the two of them share one magazine. Guarded here so
    // no clone site can reintroduce it by forgetting to strip the field.
    if (used.has(id)) id = nextWeaponId();
    used.add(id);
    weapons.push(weaponFromSlot(slot, id));
    weaponAmmo[id] = withinMagazine(slot.ammo);
    slotOrder.push(id);
  }

  return { weapons, weaponAmmo, slotOrder };
}

/**
 * Write a whole edited grid back onto a combatant.
 *
 * Deliberately touches BOTH halves - grid edits are the one interaction
 * that legitimately changes a weapon (sheet) and its ammo (state) at
 * once. Ammo for weapons that survived the edit is carried over from the
 * slot, which is where the live count already lived.
 */
export function applyLoadoutSlots(c: Combatant, slots: Slot[]): Combatant {
  const { weapons, weaponAmmo, slotOrder } = slotsToWeapons(slots, c.slotOrder ?? []);
  // "Typed by hand" is judged against the sheet the plates were SHOWN from: the effective one,
  // which wears the stances that are up (`projectLoadout`). Judged against the stored sheet, a
  // stance's +10 on every plate would read as typed, and be written into the weapons for good.
  // What is WRITTEN is still the stored sheet.
  const shown = effectiveSheet(c);
  const baselines: Baselines = { provisional: { ...shown, weapons }, bare: withoutWeaponOverrides(shown) };
  // The armed plates, in the same order as `weapons` and `slotOrder`.
  // Indexing `slots` directly would be wrong the moment an empty plate is
  // present - slotOrder is dense and `slots` need not be.
  const armed = slots.filter((s) => s && !s.isEmpty);
  const stored = new Map((c.sheet.weapons ?? []).map((w) => [w.id, w]));
  const owned = weapons.map((w, cell) => {
    const slot = armed[cell];
    return slot ? withOwnRulings(withOverrides(w, slot, baselines), slot, stored.get(w.id), shown) : w;
  });

  return {
    ...c,
    sheet: { ...c.sheet, weapons: owned, updatedAt: Date.now() },
    state: { ...c.state, weaponAmmo },
    slotOrder,
    updatedAt: Date.now(),
  };
}

/**
 * The two sheets a weapon's derived values are judged against.
 *
 * SECOND PASS - which of the four character-derived fields the GM
 * actually changed. A field still equal to what the character derives is
 * NOT stored, and that omission is the feature: it is what keeps an
 * untouched weapon tracking its owner, so promoting a marine's BS moves
 * every weapon he has not overridden. Storing all four unconditionally
 * would freeze every weapon at the value it happened to have when it was
 * last opened.
 *
 * `provisional` is the sheet AS IT WILL BE - the weapons just rebuilt,
 * before any override is attached - so the write side and
 * `resolveAttackerStats` cannot disagree about what "derived" means.
 * Comparing against the pre-edit sheet would go wrong the moment an edit
 * added a weapon.
 *
 * TWO BASELINES, because the editor and the write side resolve from
 * different sheets. The editor shows "the character's value" for the
 * weapon AS IT WAS (`bare`); this runs against the weapon as it will BE.
 * Change a bolter to melee and those disagree - the field still reads
 * the Ballistic Skill it was showing, while the new baseline is Weapon
 * Skill - so comparing against one alone would read an untouched field
 * as a deliberate override and pin the weapon to the wrong
 * characteristic for ever. A value equal to EITHER baseline is a value
 * the GM was shown and left alone. Only one different from both was
 * actually typed.
 *
 * `bare` has any existing override STRIPPED, or a stored 75 would match
 * itself, read as "unchanged", and be dropped by the very save that was
 * meant to preserve it.
 */
interface Baselines {
  provisional: Character;
  bare: Character;
}

/** Typed by hand, rather than left at either value the editor showed. */
function typed(value: number | boolean, nowValue: number | boolean, wasValue: number | boolean | undefined): boolean {
  return value !== nowValue && (wasValue === undefined || value !== wasValue);
}

/**
 * The rebuilt weapon with the overrides its plate carries.
 *
 * Which weapon this WAS is no longer a question about position: under
 * identity-by-id the weapon kept its id, so it is the same weapon if the
 * old sheet had that id. A genuinely new plate has no `was`.
 */
function withOverrides(w: CharacterWeapon, slot: Slot, { provisional, bare }: Baselines): CharacterWeapon {
  const now = resolveAttackerStats(provisional, w.id);
  const was = bare.weapons.some((x) => x.id === w.id) ? resolveAttackerStats(bare, w.id) : null;
  const out: CharacterWeapon = { ...w };
  if (typed(slot.skillValue, now.skillValue, was?.skillValue)) out.skillValue = slot.skillValue;
  if (typed(slot.strengthBonus, now.strengthBonus, was?.strengthBonus)) out.strengthBonus = slot.strengthBonus;
  setMode(out, 'canSwiftAttack', {
    ruling: slot.swiftOverride,
    byHand: typed(slot.canSwiftAttack, now.canSwiftAttack, was?.canSwiftAttack),
    value: slot.canSwiftAttack,
  });
  setMode(out, 'canLightningAttack', {
    ruling: slot.lightningOverride,
    byHand: typed(slot.canLightningAttack, now.canLightningAttack, was?.canLightningAttack),
    value: slot.canLightningAttack,
  });
  return out;
}

/**
 * Righteous Fury and psy rating, kept as the weapon's OWN ruling.
 *
 * A plate shows both RESOLVED - the weapon's value, else the character's
 * (`resolveCombatWeapon`) - so writing them back as they stand pinned the
 * character's values onto every weapon on its first rack save, and a later
 * change to the character's Fury or psy rating stopped reaching it. A value
 * still equal to what the plate was shown keeps whatever the weapon stored
 * before (nothing, for a weapon that followed its character); only a value
 * changed on the plate is stored.
 */
function withOwnRulings(
  w: CharacterWeapon,
  slot: Slot,
  before: CharacterWeapon | undefined,
  sheet: Character,
): CharacterWeapon {
  const out: CharacterWeapon = { ...w };
  const furyShown = before?.righteousFury ?? sheet.righteousFury ?? true;
  if (slot.righteousFury === furyShown) {
    if (before?.righteousFury === undefined || before.righteousFury === null) delete out.righteousFury;
    else out.righteousFury = before.righteousFury;
  }
  const psyShown = (before?.psyRating || sheet.psyRating) ?? 0;
  if ((slot.psyRating ?? 0) === psyShown) out.psyRating = before?.psyRating ?? 0;
  return out;
}

/**
 * Store an attack mode's override, or clear it.
 *
 * The attack modes carry their RULING (v8.365.0): an explicit yes or no
 * is stored even when it equals what the talents derive - so a later
 * talent cannot flip a weapon the GM pinned - and "default" (null)
 * deletes the override. A slot without the tri-state (a statblock) keeps
 * the typed-by-hand heuristic.
 */
function setMode(
  out: CharacterWeapon,
  field: 'canSwiftAttack' | 'canLightningAttack',
  { ruling, byHand, value }: { ruling: boolean | null | undefined; byHand: boolean; value: boolean },
): void {
  if (ruling === null) delete out[field];
  else if (ruling !== undefined) out[field] = ruling;
  else if (byHand) out[field] = value;
}
